import { BPM_MAX, BPM_MIN, type GenerationKind, type Motion, type Shape } from '../domain/schemas';
import { COMMAND_EXAMPLES, CommandSchema, type Command, type ParseError, type ParseResult } from './commands';
import { parseAnimationSegment } from './animationGrammar';
import { parseAudioSegment } from './audioGrammar';
import { parseMotionSegment } from './motionGrammar';
import { parseSceneSegment } from './sceneGrammar';
import { parseSyncSegment } from './syncGrammar';
import { shade } from '../scene/characters';
import { isGenerateSegment, parseGenerate, parseInstrument, parseMusicSegment } from './musicGrammar';

export const MAX_PROMPT_LENGTH = 400;
export const MAX_COMMANDS = 5;

export const COLOR_NAMES: Record<string, string> = {
  red: '#ef4444',
  orange: '#f97316',
  yellow: '#facc15',
  gold: '#eab308',
  green: '#22c55e',
  teal: '#14b8a6',
  cyan: '#22d3ee',
  blue: '#3b82f6',
  navy: '#1e3a8a',
  purple: '#a78bfa',
  violet: '#8b5cf6',
  pink: '#f472b6',
  magenta: '#d946ef',
  white: '#f4f4f5',
  grey: '#71717a',
  gray: '#71717a',
  black: '#09090b',
};

const SHAPES: Record<string, Shape> = {
  circle: 'circle', circles: 'circle', dot: 'circle', ball: 'circle',
  square: 'square', squares: 'square', box: 'square',
  triangle: 'triangle', triangles: 'triangle',
};

const MOTIONS: Record<string, Motion> = {
  orbit: 'orbit', orbits: 'orbit', orbiting: 'orbit', circling: 'orbit',
  pulse: 'pulse', pulses: 'pulse', pulsing: 'pulse',
  bounce: 'bounce', bounces: 'bounce', bouncing: 'bounce',
  spin: 'spin', spins: 'spin', spinning: 'spin', rotate: 'spin', rotates: 'spin', rotating: 'spin',
};

/** Keyword sets used to recognise requests for (unavailable) generative AI features. */
const GENERATION_INTENTS: { kind: GenerationKind; pattern: RegExp }[] = [
  { kind: 'speech', pattern: /\b(voice|vocals?|speech|speak|say|narrat\w*|sing\w*|tts)\b/ },
  { kind: 'text-to-video', pattern: /\b(video|movie|film|footage|music video)\b/ },
  { kind: 'text-to-audio', pattern: /\b(sound effects?|sfx|foley|ambien\w*|field recording)\b/ },
  {
    kind: 'text-to-music',
    pattern: /\b(generate|compose|write|produce|create|make)\b.*\b(song|track|music|beat|melody|tune|phonk|instrumental)\b/,
  },
];

const fail = (error: Omit<ParseError, 'suggestions'> & { suggestions?: string[] }): ParseResult => ({
  ok: false,
  error: { suggestions: [], ...error },
});

export function normalize(input: string): string {
  return input
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/\b(please|pls|can you|could you|would you|kindly|now)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.!?]+$/g, '')
    .trim();
}

/**
 * Splits a prompt into commands. "then" and ";" always separate commands; "," and "and"
 * do too, except inside a composition request ("… with heavy 808 bass and cowbells").
 */
export function splitSegments(text: string): string[] {
  // "create a six-second animation with running and jumping" stays one segment.
  const choreography = /^(?:create|make) .*\banimation (?:with|featuring|where) /;
  if (choreography.test(text)) return [text];
  return text
    .split(/\s*(?:;|\band then\b|\bthen\b)\s*/)
    .flatMap((piece) => (isGenerateSegment(piece.trim()) ? [piece.replace(/,\s*$/, '')] : piece.split(/\s*(?:,|\band\b)\s*/)))
    .map((s) => s.trim().replace(/,$/, ''))
    .filter(Boolean);
}

/** Colour names, optionally with a dark/light modifier ("dark purple"), or #rrggbb. */
export function parseColor(token: string | undefined): string | undefined {
  if (!token) return undefined;
  const t = token.trim().replace(/^(a |an )/, '');
  if (/^#[0-9a-f]{6}$/.test(t)) return t;
  const m = t.match(/^(dark|deep|light|pale|bright)[ -](\w+)$/);
  if (m) {
    const base = COLOR_NAMES[m[2]!];
    if (!base) return undefined;
    const amount = m[1] === 'dark' ? -0.55 : m[1] === 'deep' ? -0.35 : m[1] === 'bright' ? 0.15 : 0.5;
    return shade(base, amount);
  }
  return COLOR_NAMES[t];
}

function parseNumber(raw: string): number | undefined {
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

type SegmentResult = { command: Command } | { commands: Command[] } | { error: ParseError };

const invalid = (segment: string, message: string, example: string): SegmentResult => ({
  error: { kind: 'invalid-value', message, segment, suggestions: [example] },
});

function parseSegment(segment: string): SegmentResult | null {
  let m: RegExpMatchArray | null;

  // ---- Audio-visual sync (Phase 4A): specific phrasings ("on every kick", "at bar 3",
  // "loop bars 1 through 8"), so it goes first and never claims other grammars' commands.
  const synced = parseSyncSegment(segment);
  if (synced) return synced;

  // ---- Keyframe animation (Phase 3B): first, so "make the selected object spin" is a
  // preset (not a colour) and "create a 4-second animation" is not a composition.
  const animated = parseAnimationSegment(segment);
  if (animated) return animated;

  // ---- Composition, mixing, arrangement (Phase 1 grammar)
  const composed = parseGenerate(segment);
  if (composed) return composed;
  // ---- Choreography (Phase 3C): only claims phrases with a motion verb, so scene edits
  // ("make the selected object red") still reach the scene grammar.
  const motion = parseMotionSegment(segment);
  if (motion) return motion;
  // ---- Scene editor (Phase 3A): before animation so "add a red circle" places an object.
  const sceneCommand = parseSceneSegment(segment, parseColor);
  if (sceneCommand) return sceneCommand;

  // ---- Samples and clips (Phase 2): before music so "mute the selected clip" isn't an instrument.
  const audio = parseAudioSegment(segment);
  if (audio) return audio;
  const music = parseMusicSegment(segment);
  if (music) return music;

  // ---- Transport
  if (/^(play|start)( (the )?(music|beat|loop|playback|audio))?$/.test(segment)) return { command: { type: 'music.play' } };
  if (/^(stop|halt)( (the )?(music|beat|loop|playback|audio))?$/.test(segment)) return { command: { type: 'music.stop' } };

  // ---- Tempo
  if ((m = segment.match(/^(?:set )?(?:the )?(?:bpm|tempo)(?: to| at| =)? (-?[\d.]+)(?: ?bpm)?$/))) {
    const bpm = parseNumber(m[1]!);
    if (bpm === undefined || !Number.isInteger(bpm) || bpm < BPM_MIN || bpm > BPM_MAX) {
      return invalid(segment, `BPM must be a whole number between ${BPM_MIN} and ${BPM_MAX}.`, 'set bpm to 128');
    }
    return { command: { type: 'music.setBpm', bpm } };
  }
  if ((m = segment.match(/^(faster|slower|speed up|slow down)(?: by (\d+)(?: ?bpm)?)?$/))) {
    const amount = m[2] ? Number(m[2]) : 10;
    if (amount < 1 || amount > 100) return invalid(segment, 'Tempo change must be between 1 and 100 BPM.', 'faster by 10');
    const sign = m[1] === 'faster' || m[1] === 'speed up' ? 1 : -1;
    return { command: { type: 'music.nudgeBpm', delta: sign * amount } };
  }

  // ---- Swing
  if ((m = segment.match(/^(?:set )?swing(?: to)? ([\d.]+) ?(%?)$/))) {
    const value = Number(m[1]);
    const swing = m[2] === '%' || value > 1 ? value / 100 : value;
    if (!Number.isFinite(swing) || swing < 0 || swing > 0.5) return invalid(segment, 'Swing must be between 0% and 50%.', 'swing 20%');
    return { command: { type: 'music.setSwing', swing } };
  }
  if (/^(no swing|remove swing|straight)$/.test(segment)) return { command: { type: 'music.setSwing', swing: 0 } };

  // ---- Instruments
  if ((m = segment.match(/^(mute|unmute|solo) (?:the )?(.+)$/))) {
    const instrument = parseInstrument(m[2]!);
    if (m[1] !== 'solo' && instrument) return { command: { type: 'music.mute', instrument, muted: m[1] === 'mute' } };
    if (m[1] !== 'solo') return invalid(segment, `Unknown instrument "${m[2]}". Use kick, snare, clap, hats, perc, 808, cowbell, lead or pad.`, 'mute hats');
  }
  if ((m = segment.match(/^(?:clear|remove|empty) (?:the )?(.+?)(?: pattern| track)?$/))) {
    const instrument = parseInstrument(m[1]!);
    if (instrument) return { command: { type: 'music.clear', instrument } };
    if (/^(shapes?|scene|all shapes|everything from the scene)$/.test(m[1]!)) return { command: { type: 'animation.clearShapes' } };
  }
  if (/^(reset|restore) (?:the )?(pattern|groove|beat|drums)$/.test(segment)) return { command: { type: 'music.resetPattern' } };
  if ((m = segment.match(/^(?:randomi[sz]e|shuffle|random)(?: (?:the )?(?:drums|pattern|beat|groove))?(?: (?:with )?seed (\S+))?$/))) {
    if (m[1] === undefined) return { command: { type: 'music.randomize' } };
    const seed = parseNumber(m[1]);
    if (seed === undefined || !Number.isInteger(seed) || seed < 0 || seed > 2 ** 31 - 1) {
      return invalid(segment, 'Seed must be a non-negative whole number.', 'randomize drums seed 42');
    }
    return { command: { type: 'music.randomize', seed } };
  }

  // ---- Animation transport
  if (/^(start|play|resume|run) (?:the )?(animation|scene|visuals?)$/.test(segment) || segment === 'animate') {
    return { command: { type: 'animation.start' } };
  }
  if (/^(pause|stop|freeze) (?:the )?(animation|scene|visuals?)$/.test(segment)) return { command: { type: 'animation.pause' } };
  if (/^(reset|restart|rewind) (?:the )?(animation|scene|visuals?)$/.test(segment)) return { command: { type: 'animation.reset' } };

  // ---- Animation properties
  if ((m = segment.match(/^(?:set |make |change )?(?:the )?(?:background|bg)(?: colou?r)?(?: to| =)? (.+)$/))) {
    const color = parseColor(m[1]!.replace(/^(a |an )/, ''));
    if (!color) return invalid(segment, `Unknown colour "${m[1]}". Use a name like navy or a hex code like #112233.`, 'set background to navy');
    return { command: { type: 'animation.setBackground', color } };
  }
  if ((m = segment.match(/^(?:set )?(?:the )?(?:animation )?speed(?: to| =)? ([\d.]+)x?$/))) {
    const speed = Number(m[1]);
    if (!Number.isFinite(speed) || speed < 0.1 || speed > 5) return invalid(segment, 'Speed must be between 0.1 and 5.', 'set speed to 1.5');
    return { command: { type: 'animation.setSpeed', speed } };
  }
  if ((m = segment.match(/^(?:add|draw|create|spawn) (.+)$/))) {
    const words = m[1]!.split(' ').filter((w) => !['a', 'an', 'one', 'new', 'that', 'which', 'and', 'is', 'shape'].includes(w));
    let shape: Shape | undefined;
    let color: string | undefined;
    let motion: Motion | undefined;
    for (const word of words) {
      if (SHAPES[word]) shape = SHAPES[word];
      else if (MOTIONS[word]) motion = MOTIONS[word];
      else if (parseColor(word)) color = parseColor(word);
      else return invalid(segment, `I don't understand "${word}" in this shape description.`, 'add a red circle that orbits');
    }
    if (!shape) return invalid(segment, 'Say which shape to add: circle, square or triangle.', 'add a blue square that spins');
    return { command: { type: 'animation.addShape', shape, color: color ?? '#a78bfa', motion: motion ?? 'orbit' } };
  }
  return null;
}

function presetAsStep(preset: 'idle' | 'walk' | 'run' | 'jump' | 'float' | 'spin'): Command {
  const character = preset === 'idle' || preset === 'walk' || preset === 'run';
  return { type: 'motion.step', template: preset, target: character ? { kind: 'character' } : { kind: 'selected' }, params: {} };
}

/** Ranks documented examples by shared words with the input, for "did you mean" hints. */
export function suggest(text: string, limit = 3): string[] {
  const words = new Set(text.split(/\W+/).filter((w) => w.length > 2));
  return COMMAND_EXAMPLES.map(({ example }) => ({
    example,
    score: example.split(' ').filter((w) => words.has(w)).length,
  }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.example);
}

/**
 * Deterministic parser for a small, documented command language (see COMMAND_EXAMPLES).
 * It never guesses: input that is not fully understood is rejected as a whole, so a
 * prompt either executes completely or not at all.
 */
export function parsePrompt(input: string): ParseResult {
  if (input.length > MAX_PROMPT_LENGTH) {
    return fail({ kind: 'too-long', message: `Prompts are limited to ${MAX_PROMPT_LENGTH} characters.` });
  }
  const text = normalize(input);
  // Quoted phrases in their original case, keyed by how they look after normalisation.
  const originalText = new Map<string, string>();
  for (const m of input.matchAll(/["“']([^"”']{1,200})["”']/g)) originalText.set(normalize(m[1]!), m[1]!.trim());
  if (!text) return fail({ kind: 'empty', message: 'Type a command, for example "set bpm to 120".' });

  const segments = splitSegments(text);
  if (segments.length > MAX_COMMANDS) {
    return fail({ kind: 'too-many', message: `Up to ${MAX_COMMANDS} commands can be combined in one prompt.` });
  }
  const commands: Command[] = [];
  for (const segment of segments) {
    const result = parseSegment(segment);
    if (!result || 'error' in result) {
      // Composition requests already carry a specific explanation (procedural composer).
      if (result && isGenerateSegment(segment)) return { ok: false, error: result.error };
      // Requests for unavailable generative features are explained rather than misparsed.
      const intent = GENERATION_INTENTS.find((g) => g.pattern.test(segment));
      if (intent) {
        return fail({
          kind: 'generation-unavailable',
          generationKind: intent.kind,
          segment,
          message: `That needs ${intent.kind} generation, which has no configured provider. Nothing was generated. Local procedural composition supports brazilian phonk, drift phonk, dark phonk, trap and lo-fi.`,
          suggestions: ['create a 60-second drift phonk arrangement', 'randomize drums'],
        });
      }
      if (result) return { ok: false, error: result.error };
      return fail({
        kind: 'unsupported',
        segment,
        message: `"${segment}" is not a supported command.`,
        suggestions: suggest(segment),
      });
    }
    for (const command of 'commands' in result ? result.commands : [result.command]) {
      const parsed = CommandSchema.safeParse(command);
      if (!parsed.success) {
        return fail({ kind: 'invalid-value', segment, message: 'That command has an invalid value.' });
      }
      // Text for scene objects keeps the capitalisation it was typed with (parsing lowercases).
      if (parsed.data.type === 'scene.addObject' && parsed.data.text) {
        const original = originalText.get(parsed.data.text);
        if (original) parsed.data.text = original;
      }
      commands.push(parsed.data);
    }
  }
  // In a prompt with choreography steps, Phase 3B preset phrases become timed steps too, so
  // "make the character run, then jump, then wave" is one sequence.
  if (commands.some((c) => c.type === 'motion.step')) {
    return { ok: true, commands: commands.map((c) => (c.type === 'anim.preset' ? presetAsStep(c.preset) : c)) };
  }
  return { ok: true, commands };
}
